import {
    DecoratorNode,
    DOMConversionMap,
    DOMExportOutput,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
    Spread,
} from 'lexical'
import { ReactNode } from 'react'
import { type HelperArg, helperPillLabel, serializeHelperArgs } from './variableUtils'
import HelperPillContent from './HelperPillContent'

export type SerializedHelperNode = Spread<{
    helperName: string
    args: HelperArg[]
}, SerializedLexicalNode>

export class HelperNode extends DecoratorNode<ReactNode> {
    __helperName: string
    __args: HelperArg[]

    static getType(): string {
        return 'helper'
    }

    static clone(node: HelperNode): HelperNode {
        return new HelperNode(node.__helperName, node.__args, node.__key)
    }

    constructor(helperName: string, args: HelperArg[], key?: NodeKey) {
        super(key)
        this.__helperName = helperName
        this.__args = args
    }

    createDOM(): HTMLElement {
        const span = document.createElement('span')
        span.className = 'helper-pill'
        return span
    }

    updateDOM(): boolean {
        return false
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement('span')
        element.textContent = `{{${serializeHelperArgs(this.__helperName, this.__args)}}}`
        return { element }
    }

    static importDOM(): DOMConversionMap | null {
        return null
    }

    static importJSON(serializedNode: SerializedHelperNode): HelperNode {
        return $createHelperNode(serializedNode.helperName, serializedNode.args)
    }

    exportJSON(): SerializedHelperNode {
        return {
            type: 'helper',
            version: 1,
            helperName: this.__helperName,
            args: this.__args,
        }
    }

    getTextContent(): string {
        return `{{${serializeHelperArgs(this.__helperName, this.__args)}}}`
    }

    isInline(): boolean {
        return true
    }

    setArgs(args: HelperArg[]): void {
        const writable = this.getWritable()
        writable.__args = args
    }

    decorate(): ReactNode {
        return <HelperPillContent
            nodeKey={this.__key}
            helperName={this.__helperName}
            args={this.__args}
            label={helperPillLabel(this.__helperName, this.__args)}
        />
    }
}

export const $createHelperNode = (helperName: string, args: HelperArg[]): HelperNode => {
    return new HelperNode(helperName, args)
}

export const $isHelperNode = (node: LexicalNode | null | undefined): node is HelperNode => {
    return node instanceof HelperNode
}
